import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ShoppingBag,
  ArrowRight,
  Trash2,
  Plus,
  Minus,
  Tag,
  ShieldCheck,
  AlertTriangle,
  ArrowLeft,
  Sparkles,
  CheckCircle,
} from 'lucide-react'
import { formatMoney, formatQuantity } from '../api/client'
import { useCart } from '../state/cart'
import { useTranslation } from '../i18n/LanguageContext'

export function Cart() {
  const { cart, loading, error, subtotalLabel, setQuantity, remove, applyCoupon, removeCoupon, clearError } =
    useCart()
  const { t, isHindi } = useTranslation()
  const [coupon, setCoupon] = useState('')
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
        <p className="text-sm font-medium text-stone-500">{isHindi ? 'आपकी टोकरी लोड हो रही है…' : 'Loading your basket…'}</p>
      </div>
    )
  }

  if (!cart || cart.lines.length === 0) {
    return (
      <div className="card mx-auto my-8 max-w-md p-8 text-center sm:my-14 sm:p-12">
        <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl bg-emerald-50 text-emerald-700 shadow-inner">
          <ShoppingBag className="h-10 w-10" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-stone-900">{t('cart_empty_title')}</h1>
        <p className="mt-2 text-xs text-stone-500 leading-relaxed">
          {t('cart_empty_desc')}
        </p>
        <Link to="/" className="btn-primary mt-6 inline-flex items-center gap-2 py-3 px-6 text-sm font-bold shadow-md shadow-emerald-700/20">
          <span>{t('cart_start_shopping')}</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    )
  }

  const subtotalPaise = cart.estimatedSubtotalPaise ?? 0
  const freeDeliveryThresholdPaise = 49900
  const isFreeDelivery = subtotalPaise >= freeDeliveryThresholdPaise
  const progressPercent = Math.min(100, Math.round((subtotalPaise / freeDeliveryThresholdPaise) * 100))
  const remainingForFreeDelivery = Math.max(0, freeDeliveryThresholdPaise - subtotalPaise)

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-black text-stone-900 sm:text-3xl">{t('cart_title')}</h1>
          <p className="text-xs text-stone-500">
            {isHindi
              ? `आपकी टोकरी में ${cart.lineCount} सामान ${cart.lineCount === 1 ? 'है' : 'हैं'}`
              : `${cart.lineCount} ${cart.lineCount === 1 ? 'item' : 'items'} in your cart`}
          </p>
        </div>
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 hover:text-emerald-900 hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>{t('cart_continue_shopping')}</span>
        </Link>
      </div>

      {/* Free Delivery Bar */}
      <div className="rounded-2xl border border-emerald-200/80 bg-gradient-to-r from-emerald-50 to-teal-50 p-4">
        <div className="flex items-center justify-between text-xs font-bold text-emerald-900">
          <span className="flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            {isFreeDelivery
              ? t('cart_free_delivery_unlocked')
              : t('cart_free_delivery_add_more', { amount: formatMoney(remainingForFreeDelivery) })}
          </span>
          <span>{progressPercent}%</span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-emerald-200/60">
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-700 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Unavailable Items Banner */}
      {cart.hasUnavailableLines && (
        <div role="alert" className="flex items-start gap-2.5 rounded-2xl bg-amber-50 p-4 text-xs font-semibold text-amber-900 border border-amber-200">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700" />
          <div>
            {t('cart_unavailable_notice')}
          </div>
        </div>
      )}

      {/* Error alert */}
      {error && (
        <div role="alert" className="flex items-center justify-between rounded-2xl bg-red-50 p-4 text-xs font-medium text-red-800">
          <span>{error}</span>
          <button type="button" className="font-bold underline" onClick={clearError}>
            {isHindi ? 'हटाएं' : 'Dismiss'}
          </button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr,360px]">
        {/* Left Column: Cart items */}
        <div className="space-y-4">
          <div className="card divide-y divide-stone-100 overflow-hidden">
            {cart.lines.map((line) => (
              <div key={line.variantId} className="flex items-center gap-3 p-4 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="truncate text-sm font-bold text-stone-900 sm:text-base">
                    {line.name}
                  </h3>
                  <p className="money text-xs text-stone-500">
                    {formatMoney(line.unitPricePaise)} per {line.unitCode}
                  </p>

                  {!line.available && (
                    <span className="mt-1 inline-block text-[11px] font-semibold text-red-600">
                      {isHindi
                        ? `स्टोर में केवल ${formatQuantity(line.availableQuantity)} उपलब्ध है`
                        : `Only ${formatQuantity(line.availableQuantity)} available in dark store`}
                    </span>
                  )}

                  {line.requiresWeighing && (
                    <span className="mt-1 block text-[10px] text-amber-800 font-medium">
                      {isHindi ? 'खुला सामान · अंतिम राशि तौलने के बाद तय होगी' : 'Sold loose · final amount confirmed after weighing'}
                    </span>
                  )}
                </div>

                {/* Quantity Controls */}
                <div className="flex items-center rounded-xl border border-stone-200/90 bg-stone-50/70 p-1">
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-stone-700 shadow-sm transition-colors hover:bg-stone-100 active:scale-90"
                    aria-label={`Reduce ${line.name}`}
                    onClick={() => void setQuantity(line.variantId, Math.max(0, line.quantity - 1))}
                  >
                    <Minus className="h-3 w-3" />
                  </button>

                  <span className="money w-8 text-center text-xs font-bold text-stone-900">
                    {formatQuantity(line.quantity)}
                  </span>

                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-700 text-white shadow-sm transition-colors hover:bg-emerald-800 active:scale-90"
                    aria-label={`Add another ${line.name}`}
                    onClick={() => void setQuantity(line.variantId, line.quantity + 1)}
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>

                {/* Line Total & Remove */}
                <div className="text-right w-24">
                  <p className="money text-sm font-black text-stone-900 sm:text-base">
                    {formatMoney(line.lineTotalPaise)}
                  </p>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-400 hover:text-red-600 transition-colors"
                    onClick={() => void remove(line.variantId)}
                  >
                    <Trash2 className="h-3 w-3" /> {t('cart_remove')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Coupon Code Card */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 font-bold text-xs text-stone-800">
                <Tag className="h-4 w-4 text-emerald-600" />
                <span>{t('cart_coupon_title')}</span>
              </div>
              <span className="text-[11px] text-stone-400">WELCOME50, FREESHIP</span>
            </div>

            {cart.couponCode ? (
              <div className="flex items-center justify-between rounded-xl bg-emerald-50 px-3.5 py-2 text-xs font-bold text-emerald-900 border border-emerald-200">
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  {isHindi
                    ? <>कूपन <span className="underline">{cart.couponCode}</span> लागू हो गया!</>
                    : <>Coupon <span className="underline">{cart.couponCode}</span> applied!</>}
                </span>
                <button
                  type="button"
                  className="text-xs font-semibold text-red-600 hover:underline"
                  onClick={() => void removeCoupon()}
                >
                  {t('cart_coupon_remove')}
                </button>
              </div>
            ) : (
              <form
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  if (coupon.trim().length > 0) {
                    void applyCoupon(coupon.trim())
                    setCoupon('')
                  }
                }}
              >
                <input
                  className="field py-2 text-xs uppercase font-mono tracking-wider"
                  placeholder={t('cart_coupon_placeholder')}
                  value={coupon}
                  onChange={(event) => setCoupon(event.target.value)}
                />
                <button type="submit" className="btn-secondary py-2 text-xs font-bold">
                  {t('cart_coupon_apply')}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Right Column: Order Summary Card */}
        <div className="space-y-4">
          <div className="card p-5 space-y-4 shadow-md border-stone-200">
            <h2 className="font-heading text-base font-bold text-stone-900">{t('cart_bill_details')}</h2>

            <div className="space-y-2.5 text-xs text-stone-600">
              <div className="flex justify-between">
                <span>{t('cart_item_total')}</span>
                <span className="money text-stone-900 font-bold">{subtotalLabel}</span>
              </div>

              <div className="flex justify-between">
                <span>{t('cart_delivery_partner_fee')}</span>
                <span className="money font-bold">
                  {isFreeDelivery ? (
                    <span className="text-emerald-700">{t('cart_delivery_free')}</span>
                  ) : (
                    <span>₹40.00</span>
                  )}
                </span>
              </div>

              <div className="flex justify-between">
                <span>{t('cart_taxes_charges')}</span>
                <span className="text-stone-500 font-medium">{isHindi ? 'सामान में शामिल' : 'Included in items'}</span>
              </div>
            </div>

            <div className="border-t border-stone-200 pt-3">
              <div className="flex items-baseline justify-between">
                <div>
                  <span className="font-heading text-sm font-bold text-stone-900">{t('cart_to_pay')}</span>
                  <p className="text-[10px] text-stone-400">
                    {isHindi ? 'चेकआउट पर आधिकारिक राशि पुष्टि होगी' : 'Authoritative price confirmed at checkout'}
                  </p>
                </div>
                <span className="money text-xl font-black text-emerald-950 sm:text-2xl">
                  {subtotalLabel}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn-primary w-full py-3.5 text-base font-bold shadow-lg shadow-emerald-700/25 flex items-center justify-center gap-2"
              onClick={() => navigate('/checkout')}
            >
              <span>{t('cart_proceed_to_checkout')}</span>
              <ArrowRight className="h-4 w-4" />
            </button>

            <div className="flex items-center justify-center gap-1.5 text-[11px] text-stone-400 pt-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span>{t('cart_secure_checkout')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
